"use client";

import { motion } from "framer-motion";
import { ArrowRight, Download, CheckCircle2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { useTheme } from "@/hooks/useTheme";
import { ThemeSection, ThemeText } from "@/components/ui/theme-utils";
import { PrimaryButton, SecondaryButton } from "@/components/ui/buttons";

export function CTABanner() {
  const { isDark } = useTheme();

  return (
    <ThemeSection className="!py-16 sm:!py-24">
      <div className="container mx-auto px-5 sm:px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.1 }}
          transition={{ duration: 0.6 }}
          className={cn(
            "relative overflow-hidden rounded-[2rem] p-8 sm:p-14 lg:p-20 text-center",
            isDark
              ? "bg-[#0E0E14] border border-white/10"
              : "bg-stone-50 border border-stone-200/80"
          )}
        >
          {/* Background art */}
          <div
            className="pointer-events-none absolute inset-0 -z-0"
            style={{
              background: isDark
                ? "radial-gradient(circle at 20% 20%, rgba(124,58,237,0.35), transparent 50%), radial-gradient(circle at 80% 80%, rgba(236,72,153,0.25), transparent 55%), radial-gradient(circle at 50% 50%, rgba(34,211,238,0.18), transparent 60%)"
                : "radial-gradient(circle at 20% 20%, rgba(124,58,237,0.18), transparent 50%), radial-gradient(circle at 80% 80%, rgba(236,72,153,0.14), transparent 55%), radial-gradient(circle at 50% 50%, rgba(34,211,238,0.10), transparent 60%)"
            }}
          />
          <div
            className={cn(
              "pointer-events-none absolute inset-0 -z-0 opacity-30",
              isDark ? "bg-grid" : "bg-grid"
            )}
          />

          <div className="relative z-10 max-w-3xl mx-auto">
            <span
              className={cn(
                "inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium mb-6",
                isDark
                  ? "bg-white/[0.06] border border-white/10 text-white/80"
                  : "bg-white border border-stone-200 text-stone-700"
              )}
            >
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
              </span>
              Ready when you are
            </span>

            <h2 className="responsive-heading-2 mb-5">
              Stop fighting your <span className="text-gradient">uploads</span>
            </h2>
            <ThemeText
              variant="subtle"
              className="responsive-text-lg mb-8 max-w-xl mx-auto"
            >
              Book a 20-minute demo and see Zset push a 50 GB file at line rate.
              Or just download the app and feel it for yourself.
            </ThemeText>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <PrimaryButton
                href="https://cal.com/zset.in/demo"
                target="_blank"
                rel="noopener noreferrer"
                size="lg"
                icon={<ArrowRight className="h-4 w-4" />}
              >
                Book a demo
              </PrimaryButton>
              <SecondaryButton
                href="/downloads"
                size="lg"
                icon={<Download className="h-4 w-4" />}
              >
                Download the app
              </SecondaryButton>
            </div>

            <ul className="mt-8 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-stone-500">
              <li className="inline-flex items-center gap-1.5">
                <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                Free 14-day trial
              </li>
              <li className="inline-flex items-center gap-1.5">
                <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                No credit card
              </li>
              <li className="inline-flex items-center gap-1.5">
                <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                Cancel anytime
              </li>
            </ul>
          </div>
        </motion.div>
      </div>
    </ThemeSection>
  );
}
